import type { PaigeClient } from '@paige/api-client';
import { tenantHeader, unwrap } from './http';
import type {
  Billing,
  BillingLink,
  Company,
  Person,
  Package,
  PackageChangePreview,
  PartnerCreditRequest,
  RecentChange,
  Role,
  PersonJobs,
} from './types';

/**
 * The preview the admin saw, sent back with a change for a company paying by card: the server
 * prices it again at `prorationDate` and refuses when the amount differs (`PreviewChanged`).
 */
export type PreviewSeen = { amountDueNow: number; prorationDate: number | null };

/**
 * How a plan is to be paid (ADR-0029): the interval (the company's own when left out) and, for a
 * first plan on Starter, Pro or Business, by invoice instead of card.
 */
export type PlanTerms = { interval?: 'month' | 'year'; payByInvoice?: boolean };

/** The company screen: package, credits, security, people, and paying by card. */
export interface CompanyApi {
  get(tenant: string): Promise<Company>;
  /** The name or the time zone; the slug, and every job's email address, never moves. */
  update(tenant: string, change: { name?: string; timeZone?: string }): Promise<Company>;
  /** The last changes to the company, newest first (admin). */
  changes(tenant: string, limit: number): Promise<RecentChange[]>;
  /** Staff only: support sets a package by hand. Customers go through `checkout`. */
  setPackage(tenant: string, pkg: Package): Promise<Company>;
  /** Starts paying for a package by card; the answer is where to send the browser. */
  checkout(
    tenant: string,
    pkg: Package,
    confirm?: PreviewSeen,
    terms?: PlanTerms,
  ): Promise<BillingLink>;
  /** What a change of plan would charge now and when it takes effect, before the admin confirms. */
  preview(tenant: string, pkg: Package, interval?: 'month' | 'year'): Promise<PackageChangePreview>;
  /** Opens the billing portal (card, invoices, cancellation); the answer is where to send the browser. */
  portal(tenant: string): Promise<BillingLink>;
  setSecurity(tenant: string, requireMfa: boolean): Promise<Company>;
  /** Closes the company; the name typed by the admin must be the company's (400 name_mismatch). */
  close(tenant: string, name: string): Promise<void>;
  billing(tenant: string): Promise<Billing>;
  /** Starts paying for a credit pack by card at the rate of the company's own plan (the API works it out); the answer is where to send the browser. */
  packCheckout(tenant: string, credits: number): Promise<BillingLink>;
  /** Asks the partner who pays for the company for more credits (ADR-0029); the request as the partner sees it. */
  askPartner(
    tenant: string,
    ask: { credits: number | null; note: string | null },
  ): Promise<PartnerCreditRequest>;
  people(tenant: string): Promise<Person[]>;
  invite(tenant: string, email: string, role: Role): Promise<Person>;
  setRole(tenant: string, userId: string, role: Role): Promise<void>;
  /** A member by their id (yourself, to leave), an invite by its address. */
  remove(tenant: string, person: { userId?: string | null; email?: string }): Promise<void>;
  /** Emails the invitation again. */
  resendInvite(tenant: string, email: string): Promise<void>;
  /** Ends every session the person has open; they sign in again. */
  signOut(tenant: string, userId: string): Promise<void>;
  /** Takes the person's second step away; they set up a new one at their next sign-in. */
  resetSecondFactor(tenant: string, userId: string): Promise<void>;
  /** Which jobs the person may open: every job, or only these. */
  setJobs(tenant: string, userId: string, jobs: PersonJobs): Promise<void>;
}

const h = (tenant: string) => ({ params: { header: tenantHeader(tenant) } });

/** What the company screen reads: the company, its recent changes, the bill. */
function companyReadsApi(client: PaigeClient): Pick<CompanyApi, 'get' | 'changes' | 'billing'> {
  return {
    async get(tenant) {
      return unwrap(await client.GET('/company', h(tenant))) as Company;
    },
    async changes(tenant, limit) {
      return unwrap(
        await client.GET('/company/changes', {
          params: { header: tenantHeader(tenant), query: { limit } },
        }),
      ) as RecentChange[];
    },
    async billing(tenant) {
      const b = unwrap(await client.GET('/company/billing', h(tenant)));
      // A company its partner pays for is sent no price or pack (ADR-0029): read as none.
      return {
        ...b,
        packs: b.packs ?? [],
        packsBought: b.packsBought ?? [],
        pricePerCredit: b.pricePerCredit ?? 0,
      } as Billing;
    },
  };
}

/** Paying by card: a plan's checkout and its preview, a credit pack, and the billing portal. */
function cardApi(
  client: PaigeClient,
): Pick<CompanyApi, 'checkout' | 'preview' | 'portal' | 'packCheckout' | 'askPartner'> {
  return {
    async checkout(tenant, pkg, confirm, terms) {
      const body = { package: pkg, ...(confirm ? { confirm } : {}), ...(terms ?? {}) };
      return unwrap(
        await client.POST('/company/package/checkout', { ...h(tenant), body }),
      ) as BillingLink;
    },
    async preview(tenant, pkg, interval) {
      const body = interval ? { package: pkg, interval } : { package: pkg };
      return unwrap(
        await client.POST('/company/package/preview', { ...h(tenant), body }),
      ) as PackageChangePreview;
    },
    async portal(tenant) {
      return unwrap(await client.POST('/company/package/portal', h(tenant))) as BillingLink;
    },
    async packCheckout(tenant, credits) {
      return unwrap(
        await client.POST('/company/billing/packs/checkout', { ...h(tenant), body: { credits } }),
      ) as BillingLink;
    },
    async askPartner(tenant, ask) {
      return unwrap(
        await client.POST('/company/billing/credit-request', { ...h(tenant), body: ask }),
      ) as PartnerCreditRequest;
    },
  };
}

export function companyApi(client: PaigeClient): CompanyApi {
  return {
    ...peopleApi(client),
    ...personActionsApi(client),
    ...companyReadsApi(client),
    ...cardApi(client),
    async update(tenant, change) {
      return unwrap(await client.PUT('/company', { ...h(tenant), body: change })) as Company;
    },
    async setPackage(tenant, pkg) {
      return unwrap(
        await client.PUT('/company/package', { ...h(tenant), body: { package: pkg } }),
      ) as Company;
    },
    async close(tenant, name) {
      unwrap(await client.POST('/company/close', { ...h(tenant), body: { name } }));
    },
    async setSecurity(tenant, requireMfa) {
      return unwrap(
        await client.PUT('/company/security', { ...h(tenant), body: { requireMfa } }),
      ) as Company;
    },
  };
}

/** What an admin does to one person from the sheet: send the invitation again, sign them out. */
function personActionsApi(
  client: PaigeClient,
): Pick<CompanyApi, 'resendInvite' | 'signOut' | 'resetSecondFactor' | 'setJobs'> {
  return {
    async setJobs(tenant, userId, jobs) {
      unwrap(
        await client.PUT('/company/people/{userId}/jobs', {
          params: { header: tenantHeader(tenant), path: { userId } },
          body: jobs,
        }),
      );
    },
    async resetSecondFactor(tenant, userId) {
      unwrap(
        await client.POST('/company/people/{userId}/second-factor/reset', {
          params: { header: tenantHeader(tenant), path: { userId } },
        }),
      );
    },
    async resendInvite(tenant, email) {
      unwrap(
        await client.POST('/company/invites/{email}/send', {
          params: { header: tenantHeader(tenant), path: { email } },
        }),
      );
    },
    async signOut(tenant, userId) {
      unwrap(
        await client.POST('/company/people/{userId}/sign-out', {
          params: { header: tenantHeader(tenant), path: { userId } },
        }),
      );
    },
  };
}

/** The people half: list, invite, role, remove (an invite is removed by email). */
function peopleApi(
  client: PaigeClient,
): Pick<CompanyApi, 'people' | 'invite' | 'setRole' | 'remove'> {
  return {
    async people(tenant) {
      return unwrap(await client.GET('/company/people', h(tenant))) as Person[];
    },
    async invite(tenant, email, role) {
      return unwrap(
        await client.POST('/company/people', { ...h(tenant), body: { email, role } }),
      ) as Person;
    },
    async setRole(tenant, userId, role) {
      unwrap(
        await client.PATCH('/company/people/{userId}', {
          params: { header: tenantHeader(tenant), path: { userId } },
          body: { role },
        }),
      );
    },
    async remove(tenant, person) {
      if (person.userId) {
        unwrap(
          await client.DELETE('/company/people/{userId}', {
            params: { header: tenantHeader(tenant), path: { userId: person.userId } },
          }),
        );
      } else {
        unwrap(
          await client.DELETE('/company/invites/{email}', {
            params: { header: tenantHeader(tenant), path: { email: person.email ?? '' } },
          }),
        );
      }
    },
  };
}
